/**
 * Sketchplan direct-card pricing — the single source of truth.
 *
 * One configuration drives the Sketchplan app (sketchplan.rehub.studio),
 * the marketing site (sketchplan.com), Floorplans Studio professional orders,
 * checkout summaries and the seed rows for `public.sketchplan_price_bands`,
 * which the server-side `sketchplan_quote()` reads before any Stripe payment
 * is created. `sketchplan.test.ts` fails if that seed drifts from this file.
 *
 * Residential price lists are fixed per currency. GBP, USD and EUR are
 * independent published lists — never derive one from another with FX.
 * Commercial plans are double the equivalent residential price.
 *
 * Logged-in clients with `profiles.custom_plan_rate_pence` keep that
 * negotiated all-in GBP rate. This file never overwrites those rows.
 */

import {
  detectBillingCurrency,
  formatMoneyFromMinor,
  parseRehubCurrency,
  readPersistedBillingCurrency,
  type RehubCurrency,
} from './currency';

export type SketchplanPropertyType = 'residential' | 'commercial';
export type SketchplanTurnaround = 'standard' | 'express';
export type SketchplanCurrency = RehubCurrency;

/** Billing thresholds are 1,000 sq ft steps; a part step counts as a full step. */
export const SKETCHPLAN_SIZE_STEP_SQFT = 1000;

/** Properties above this size are quoted individually. */
export const SKETCHPLAN_QUOTE_SQFT = 10000;

/** Minimum usable measurement. */
export const SKETCHPLAN_MIN_SQFT = 100;

/** Commercial plans cost this multiple of the equivalent residential plan. */
export const SKETCHPLAN_COMMERCIAL_MULTIPLIER = 2;

/** The commercial surcharge as a percentage of the residential price. */
export const SKETCHPLAN_COMMERCIAL_UPLIFT_PERCENT = (SKETCHPLAN_COMMERCIAL_MULTIPLIER - 1) * 100;

/** Short label for badges and breakdown lines, e.g. "Commercial surcharge (2× residential)". */
export const SKETCHPLAN_COMMERCIAL_RULE_SHORT = `${SKETCHPLAN_COMMERCIAL_MULTIPLIER}× residential`;

/** Sentence fragment: "Commercial floor plans are double the equivalent residential price." */
export const SKETCHPLAN_COMMERCIAL_RULE = 'double the equivalent residential price';

/** Existing express uplift on the type+size price. Not a new charge. */
export const SKETCHPLAN_EXPRESS_MULTIPLIER = 1.5;

/** Size-band ids stored on orders and used by the SQL quote helper. */
export type SketchplanSizeBandId =
  | 'up_to_1000'
  | '1001_2000'
  | '2001_3000'
  | '3001_4000'
  | '4001_5000'
  | '5001_6000'
  | '6001_7000'
  | '7001_8000'
  | '8001_9000'
  | '9001_10000'
  | 'over_10000';

export interface SketchplanSizeBand {
  id: SketchplanSizeBandId;
  /** Inclusive upper bound in sq ft. null = open-ended quote band. */
  maxSqFt: number | null;
  label: string;
  /** 1 for up to 1,000 sq ft, 2 for 1,001–2,000 sq ft, … null for the quote band. */
  step: number | null;
}

function sqFtLabel(n: number): string {
  return n.toLocaleString('en-GB');
}

function buildSizeBands(): SketchplanSizeBand[] {
  const bands: SketchplanSizeBand[] = [];
  const steps = SKETCHPLAN_QUOTE_SQFT / SKETCHPLAN_SIZE_STEP_SQFT;
  for (let step = 1; step <= steps; step++) {
    const max = step * SKETCHPLAN_SIZE_STEP_SQFT;
    const min = max - SKETCHPLAN_SIZE_STEP_SQFT + 1;
    bands.push({
      id: (step === 1 ? `up_to_${max}` : `${min}_${max}`) as SketchplanSizeBandId,
      maxSqFt: max,
      label: step === 1 ? `Up to ${sqFtLabel(max)} sq ft` : `${sqFtLabel(min)}–${sqFtLabel(max)} sq ft`,
      step,
    });
  }
  bands.push({
    id: 'over_10000',
    maxSqFt: null,
    label: `Over ${sqFtLabel(SKETCHPLAN_QUOTE_SQFT)} sq ft`,
    step: null,
  });
  return bands;
}

export const SKETCHPLAN_SIZE_BANDS: SketchplanSizeBand[] = buildSizeBands();

export interface SketchplanResidentialPriceList {
  /** Minor-unit prices for up to 1,000 / 1,001–2,000 / 2,001–3,000 sq ft. */
  tiers: readonly [number, number, number];
  /** Added for each further 1,000 sq ft, or part thereof, above 3,000 sq ft. */
  additionalPer1000: number;
}

/** Fixed residential price lists in minor units (pence / cents / euro-cents). */
export const SKETCHPLAN_RESIDENTIAL_PRICE_LISTS: Record<SketchplanCurrency, SketchplanResidentialPriceList> = {
  GBP: { tiers: [1000, 1500, 2000], additionalPer1000: 500 },
  USD: { tiers: [1300, 2000, 2700], additionalPer1000: 700 },
  EUR: { tiers: [1200, 1800, 2400], additionalPer1000: 600 },
};

/** Number of 1,000 sq ft steps a size falls into (part steps round up). */
export function sketchplanSizeStep(sqFt: number): number {
  const s = Math.max(0, Math.floor(Number.isFinite(sqFt) ? sqFt : 0));
  return Math.max(1, Math.ceil(s / SKETCHPLAN_SIZE_STEP_SQFT));
}

function residentialPriceForStep(step: number, currency: SketchplanCurrency): number {
  const list = SKETCHPLAN_RESIDENTIAL_PRICE_LISTS[currency];
  if (step <= list.tiers.length) return list.tiers[step - 1];
  return list.tiers[list.tiers.length - 1] + (step - list.tiers.length) * list.additionalPer1000;
}

/** Commercial = residential × SKETCHPLAN_COMMERCIAL_MULTIPLIER. Kept in integer minor units. */
export function applyCommercialUplift(residentialMinor: number): number {
  return Math.round(residentialMinor * SKETCHPLAN_COMMERCIAL_MULTIPLIER);
}

/** Per-1,000 sq ft increment above 3,000 sq ft for a property type. */
export function sketchplanAdditionalStepMinor(
  propertyType: SketchplanPropertyType,
  currency: SketchplanCurrency = 'GBP',
): number {
  const step = SKETCHPLAN_RESIDENTIAL_PRICE_LISTS[currency].additionalPer1000;
  return propertyType === 'commercial' ? applyCommercialUplift(step) : step;
}

type PriceTable = Record<SketchplanPropertyType, Record<SketchplanSizeBandId, number | null>>;

function buildPriceTable(currency: SketchplanCurrency): PriceTable {
  const table = { residential: {}, commercial: {} } as PriceTable;
  for (const band of SKETCHPLAN_SIZE_BANDS) {
    const residential = band.step == null ? null : residentialPriceForStep(band.step, currency);
    table.residential[band.id] = residential;
    table.commercial[band.id] = residential == null ? null : applyCommercialUplift(residential);
  }
  return table;
}

/** Canonical GBP prices in pence. null = request a quote (not self-serve). */
export const SKETCHPLAN_DIRECT_CARD_PRICES: PriceTable = buildPriceTable('GBP');

/** Canonical USD prices in cents. Independent published list — not FX from GBP. */
export const SKETCHPLAN_DIRECT_CARD_PRICES_USD: PriceTable = buildPriceTable('USD');

/**
 * Canonical EUR prices in euro-cents. Independent published list. Shown to
 * eurozone visitors; checkout stays GBP/USD until a Sketchplan EUR checkout is
 * approved (see `SKETCHPLAN_CHECKOUT_CURRENCIES`).
 */
export const SKETCHPLAN_DIRECT_CARD_PRICES_EUR: PriceTable = buildPriceTable('EUR');

export const SKETCHPLAN_DIRECT_CARD_PRICES_BY_CURRENCY: Record<SketchplanCurrency, PriceTable> = {
  GBP: SKETCHPLAN_DIRECT_CARD_PRICES,
  EUR: SKETCHPLAN_DIRECT_CARD_PRICES_EUR,
  USD: SKETCHPLAN_DIRECT_CARD_PRICES_USD,
};

export interface SketchplanPriceTableRow {
  key: string;
  label: string;
  residentialMinor: number | null;
  commercialMinor: number | null;
  /** True for the "+X per additional 1,000 sq ft" row (an increment, not a total). */
  increment: boolean;
}

/**
 * Compact published price table: the three fixed tiers, the per-1,000 sq ft
 * increment above them, and the quote band. Every pricing table renders this.
 */
export function sketchplanPriceTableRows(currency: SketchplanCurrency = 'GBP'): SketchplanPriceTableRow[] {
  const table = SKETCHPLAN_DIRECT_CARD_PRICES_BY_CURRENCY[currency];
  const tierCount = SKETCHPLAN_RESIDENTIAL_PRICE_LISTS[currency].tiers.length;
  const tiers = SKETCHPLAN_SIZE_BANDS.slice(0, tierCount).map((band) => ({
    key: band.id,
    label: band.label,
    residentialMinor: table.residential[band.id],
    commercialMinor: table.commercial[band.id],
    increment: false,
  }));
  const lastTierMax = sqFtLabel(tierCount * SKETCHPLAN_SIZE_STEP_SQFT);
  const quoteBand = SKETCHPLAN_SIZE_BANDS[SKETCHPLAN_SIZE_BANDS.length - 1];
  return [
    ...tiers,
    {
      key: 'additional',
      label: `Each additional ${sqFtLabel(SKETCHPLAN_SIZE_STEP_SQFT)} sq ft (or part) above ${lastTierMax} sq ft`,
      residentialMinor: sketchplanAdditionalStepMinor('residential', currency),
      commercialMinor: sketchplanAdditionalStepMinor('commercial', currency),
      increment: true,
    },
    { key: quoteBand.id, label: quoteBand.label, residentialMinor: null, commercialMinor: null, increment: false },
  ];
}

/** Currencies that can actually be charged today. EUR is display-only. */
export const SKETCHPLAN_CHECKOUT_CURRENCIES: SketchplanCurrency[] = ['GBP', 'USD'];
export const SKETCHPLAN_DISPLAY_CURRENCIES: SketchplanCurrency[] = ['GBP', 'EUR', 'USD'];

/** Lowest self-serve residential price per currency (up to 1,000 sq ft). */
export const SKETCHPLAN_FROM_PRICE_MINOR: Record<SketchplanCurrency, number> = {
  GBP: SKETCHPLAN_DIRECT_CARD_PRICES.residential.up_to_1000 as number,
  EUR: SKETCHPLAN_DIRECT_CARD_PRICES_EUR.residential.up_to_1000 as number,
  USD: SKETCHPLAN_DIRECT_CARD_PRICES_USD.residential.up_to_1000 as number,
};

/** Lowest self-serve commercial price per currency (up to 1,000 sq ft). */
export const SKETCHPLAN_FROM_COMMERCIAL_MINOR: Record<SketchplanCurrency, number> = {
  GBP: SKETCHPLAN_DIRECT_CARD_PRICES.commercial.up_to_1000 as number,
  EUR: SKETCHPLAN_DIRECT_CARD_PRICES_EUR.commercial.up_to_1000 as number,
  USD: SKETCHPLAN_DIRECT_CARD_PRICES_USD.commercial.up_to_1000 as number,
};

/** Lowest self-serve residential / commercial prices (GBP pence). */
export const SKETCHPLAN_FROM_PRICE_PENCE = SKETCHPLAN_FROM_PRICE_MINOR.GBP;
export const SKETCHPLAN_FROM_COMMERCIAL_PENCE = SKETCHPLAN_FROM_COMMERCIAL_MINOR.GBP;

/** Lowest self-serve residential / commercial prices (USD cents). */
export const SKETCHPLAN_FROM_PRICE_USD_CENTS = SKETCHPLAN_FROM_PRICE_MINOR.USD;
export const SKETCHPLAN_FROM_COMMERCIAL_USD_CENTS = SKETCHPLAN_FROM_COMMERCIAL_MINOR.USD;

/**
 * Tax treatment used by checkout copy and the Stripe charge.
 *
 * Stripe Checkout is created with `automatic_tax` off and the listed
 * amount as `unit_amount`. The amount shown before payment is the amount
 * charged. GBP list prices are treated as VAT-inclusive. USD list prices
 * are charged as published (no extra tax added at checkout).
 */
export const SKETCHPLAN_VAT = {
  mode: 'inclusive' as const,
  label: 'Includes VAT',
  summary: 'GBP prices include VAT. The amount shown is the amount charged.',
};

export function sketchplanChargeSummary(currency: SketchplanCurrency): string {
  if (currency === 'USD') {
    return 'USD prices are charged as listed. The amount shown is the amount charged.';
  }
  if (currency === 'EUR') {
    return 'EUR prices are shown as listed. EUR checkout is not yet available — pay in pounds or US dollars.';
  }
  return SKETCHPLAN_VAT.summary;
}

export const SKETCHPLAN_CURRENCY: SketchplanCurrency = 'GBP';

/**
 * Fulfilment facts used by the app order form and the marketing site.
 * Standard/express hours and the 50% express uplift match GuestOrderFlow.
 *
 * The delivery clock starts when payment is received and the order is
 * confirmed. There is no published rule stating whether weekends or UK bank
 * holidays are excluded — do not invent one in customer copy.
 */
export const SKETCHPLAN_TURNAROUND = {
  standardHours: 48,
  expressHours: 24,
  expressSurchargePercent: 50,
  standardLabel: 'Standard delivery: 48 hours',
  expressLabel: 'Express: 24 hours, with a 50% surcharge',
  standardOption: 'Standard (48 hours)',
  expressOption: 'Express (24 hours, +50%)',
  begins:
    'The 48-hour or 24-hour delivery period starts once payment is received and the order is confirmed.',
} as const;

export const SKETCHPLAN_OUTPUT_FORMATS = ['JPG', 'PNG', 'PDF'] as const;

export const SKETCHPLAN_OUTPUT = {
  formats: SKETCHPLAN_OUTPUT_FORMATS,
  formatsLabel: 'JPG, PNG or PDF',
  description:
    'You receive a high-resolution CAD-style drawing as a JPG, PNG or PDF, suitable for property portals and brochures. Editable CAD files such as DWG are not included.',
} as const;

export const SKETCHPLAN_OPERATOR = {
  name: 'ReHub Studio',
  relationship:
    'Sketchplan is operated as part of ReHub Studio, the same group that runs Photoplan. Photoplan is the on-site property media service; Sketchplan is the remote sketch-redraw option when you already have measurements.',
  human:
    'A person redraws your sketch and checks it for sense. This is not an automatic trace of your lines.',
} as const;

/** Approx. 1 m² = 10.7639 sq ft. Billing thresholds stay in square feet. */
export const SQFT_PER_SQM = 10.7639;

export function sqFtToApproxSqM(sqFt: number): number {
  return Math.round(sqFt / SQFT_PER_SQM);
}

export function sqMToSqFt(sqM: number): number {
  return Math.round(sqM * SQFT_PER_SQM);
}

export function sizeBandMetricHint(band: SketchplanSizeBand): string {
  const idx = SKETCHPLAN_SIZE_BANDS.findIndex((item) => item.id === band.id);
  if (band.maxSqFt == null) {
    return `over approx. ${sqFtToApproxSqM(SKETCHPLAN_QUOTE_SQFT).toLocaleString('en-GB')} m²`;
  }
  if (idx <= 0) {
    return `up to approx. ${sqFtToApproxSqM(band.maxSqFt)} m²`;
  }
  const previousMax = SKETCHPLAN_SIZE_BANDS[idx - 1]?.maxSqFt;
  if (previousMax == null) {
    return `up to approx. ${sqFtToApproxSqM(band.maxSqFt)} m²`;
  }
  return `approx. ${sqFtToApproxSqM(previousMax)}–${sqFtToApproxSqM(band.maxSqFt)} m²`;
}

function formatSketchplanMoney(minorUnits: number, currency: SketchplanCurrency = 'GBP'): string {
  return formatMoneyFromMinor(minorUnits, currency);
}

export function formatGBP(pence: number): string {
  return formatSketchplanMoney(pence, 'GBP');
}

export function formatUSD(cents: number): string {
  return formatSketchplanMoney(cents, 'USD');
}

/** "£10", "$13", "€12" — the headline starting price for a currency. */
export function formatSketchplanFromPrice(currency: SketchplanCurrency = 'GBP'): string {
  return formatSketchplanMoney(SKETCHPLAN_FROM_PRICE_MINOR[currency], currency);
}

export const SKETCHPLAN_SUPPORTING_COPY =
  'Upload your sketch and we\u2019ll turn it into a clean, professional floor plan.';

export const SKETCHPLAN_AMENDMENTS = {
  short: 'One round of amendments included.',
  long:
    'One reasonable round of amendments is included in the price. Simple corrections so the plan matches the sketch you originally supplied are never charged again. Substantial changes, or work outside the original brief, are quoted separately before any work starts.',
} as const;

/** Customer-facing pricing copy, localised to the display currency. */
export function sketchplanPricingCopy(currency: SketchplanCurrency = 'GBP') {
  const from = formatSketchplanFromPrice(currency);
  const commercialFrom = formatSketchplanMoney(SKETCHPLAN_FROM_COMMERCIAL_MINOR[currency], currency);
  const firstBand = sqFtLabel(SKETCHPLAN_SIZE_STEP_SQFT);
  const residentialStep = formatSketchplanMoney(sketchplanAdditionalStepMinor('residential', currency), currency);
  const tax = currency === 'GBP' ? ' including VAT' : '';
  return {
    from,
    commercialFrom,
    headline: `Professional floor plans from just ${from}`,
    supporting: SKETCHPLAN_SUPPORTING_COPY,
    fromSize: `From ${from} for properties up to ${firstBand} sq ft.`,
    residentialFrom: `Residential plans from ${from}${tax}.`,
    commercialFromLine: `Commercial plans from ${commercialFrom}${tax}.`,
    commercialSurcharge: `Commercial floor plans are ${SKETCHPLAN_COMMERCIAL_RULE}.`,
    sizeRule: `Priced in ${firstBand} sq ft steps. Above 3,000 sq ft, add ${residentialStep} for each additional ${firstBand} sq ft or part thereof.`,
  };
}

const GBP_COPY = sketchplanPricingCopy('GBP');
const USD_COPY = sketchplanPricingCopy('USD');
const EUR_COPY = sketchplanPricingCopy('EUR');

/** "£15 / $20 / €18" for the residential tier at a given 1,000 sq ft step. */
function allCurrencyTier(step: number): string {
  return (['GBP', 'USD', 'EUR'] as const)
    .map((c) => formatSketchplanMoney(residentialPriceForStep(step, c), c))
    .join(' / ');
}

function allCurrencyStep(): string {
  return (['GBP', 'USD', 'EUR'] as const)
    .map((c) => formatSketchplanMoney(sketchplanAdditionalStepMinor('residential', c), c))
    .join(' / ');
}

export const SKETCHPLAN_PRICING_MESSAGE =
  `Professional floor plans from just ${GBP_COPY.from} / ${USD_COPY.from} / ${EUR_COPY.from} · Commercial plans ${SKETCHPLAN_COMMERCIAL_RULE_SHORT}`;

/** GBP-first static copy for server-rendered and non-localised surfaces. */
export const SKETCHPLAN_PRICING_COPY = {
  message: SKETCHPLAN_PRICING_MESSAGE,
  headline: GBP_COPY.headline,
  supporting: SKETCHPLAN_SUPPORTING_COPY,
  fromSize: GBP_COPY.fromSize,
  subhead: `${SKETCHPLAN_SUPPORTING_COPY} ${GBP_COPY.fromSize}`,
  gbpFromResidential: GBP_COPY.residentialFrom,
  gbpFromCommercial: GBP_COPY.commercialFromLine,
  commercialSurcharge: GBP_COPY.commercialSurcharge,
  sizeRule: GBP_COPY.sizeRule,
  deliveryStandard: SKETCHPLAN_TURNAROUND.standardLabel,
  amendment: SKETCHPLAN_AMENDMENTS.short,
  amendmentLong: SKETCHPLAN_AMENDMENTS.long,
  noLogin:
    'Order and pay first. Create an account or sign in afterwards to manage your project and download your plans.',
  oneProperty:
    'The price is for one completed property floor-plan order. Multiple floors are included within the property’s total size — there is no extra per-floor charge.',
  area:
    'Use the property’s total internal floor area, adding every storey together. Billing thresholds are in square feet; square-metre figures are approximate conversions only.',
  accuracy:
    'Sketchplan is a professional redrawing service, not a measured survey. You supply the measurements; we redraw the plan. The completed drawing will only be as accurate as the sketch, measurements and information supplied. It is intended for listings, brochures and marketing, not for planning applications, building work or legal documents.',
  vat: SKETCHPLAN_VAT.summary,
  output: SKETCHPLAN_OUTPUT.description,
  accountVsCard:
    'Published prices are for direct-card orders. Existing clients with an agreed account rate keep that rate when they are signed in. Contact us to discuss account pricing for regular work. There is no public automatic volume-discount scheme on direct-card orders.',
  cadStyle:
    'Finished plans are CAD-style drawings for marketing use. They are not editable CAD files (DWG or similar) unless that is arranged separately.',
} as const;

export const SKETCHPLAN_PUBLIC_FAQS: { question: string; answer: string }[] = [
  {
    question: 'How much does a floor plan cost?',
    answer:
      `Residential floor plans start at ${allCurrencyTier(1)} for properties up to 1,000 sq ft. 1,001–2,000 sq ft is ${allCurrencyTier(2)} and 2,001–3,000 sq ft is ${allCurrencyTier(3)}. Above 3,000 sq ft, add ${allCurrencyStep()} for each additional 1,000 sq ft or part thereof. Properties over ${sqFtLabel(SKETCHPLAN_QUOTE_SQFT)} sq ft are quoted individually.`,
  },
  {
    question: 'How are commercial floor plans priced?',
    answer:
      `Commercial floor plans are ${SKETCHPLAN_COMMERCIAL_RULE}, so they start at ${GBP_COPY.commercialFrom} / ${USD_COPY.commercialFrom} / ${EUR_COPY.commercialFrom}. The commercial surcharge is shown in your order summary before you pay.`,
  },
  {
    question: 'Do I need an account to order?',
    answer:
      'No. Order and pay first. After payment, create an account or sign in with the same email to manage the project and download the completed plans.',
  },
  {
    question: 'What is included in the price?',
    answer:
      `The price is for one completed property floor-plan order. ${SKETCHPLAN_AMENDMENTS.long} Multiple floors are included within the property's total size. You receive a high-resolution CAD-style drawing as a JPG, PNG or PDF. Editable CAD files are not included.`,
  },
  {
    question: 'Will I be charged for corrections?',
    answer: SKETCHPLAN_AMENDMENTS.long,
  },
  {
    question: 'How long does delivery take?',
    answer:
      'Standard delivery is 48 hours and Express is 24 hours with a 50% surcharge. The period starts once payment is received and the order is confirmed.',
  },
  {
    question: 'Is Sketchplan a measured survey?',
    answer:
      'No. Sketchplan is a professional redrawing service, not a measured survey. You provide the measurements; we redraw the plan. The completed drawing will only be as accurate as the information you supply, and it is intended for listings, brochures and marketing rather than planning or legal use.',
  },
  {
    question: 'Is tax included?',
    answer:
      'GBP prices include VAT. USD prices are charged as listed. EUR prices are shown for reference; EUR checkout is not yet available. The amount shown at checkout is the amount charged.',
  },
  {
    question: 'Do you offer volume discounts?',
    answer:
      'Direct-card orders use the published prices. Existing clients with an agreed account rate keep that rate when signed in. Contact us if you need account pricing for a regular programme of work.',
  },
];

export function getSizeBand(sqFt: number): SketchplanSizeBand {
  const s = Math.max(0, Math.floor(Number.isFinite(sqFt) ? sqFt : 0));
  if (s > SKETCHPLAN_QUOTE_SQFT) return SKETCHPLAN_SIZE_BANDS[SKETCHPLAN_SIZE_BANDS.length - 1];
  return SKETCHPLAN_SIZE_BANDS[sketchplanSizeStep(s) - 1];
}

export function getSizeBandById(id: string | null | undefined): SketchplanSizeBand | null {
  return SKETCHPLAN_SIZE_BANDS.find((band) => band.id === id) ?? null;
}

export function listPriceMinor(
  propertyType: SketchplanPropertyType,
  sizeBandId: SketchplanSizeBandId,
  currency: SketchplanCurrency = 'GBP',
): number | null {
  return SKETCHPLAN_DIRECT_CARD_PRICES_BY_CURRENCY[currency][propertyType][sizeBandId] ?? null;
}

/** @deprecated Prefer listPriceMinor(..., 'GBP') */
export function listPricePence(
  propertyType: SketchplanPropertyType,
  sizeBandId: SketchplanSizeBandId,
): number | null {
  return listPriceMinor(propertyType, sizeBandId, 'GBP');
}

export function normalizeSketchplanCurrency(value: unknown): SketchplanCurrency {
  return parseRehubCurrency(String(value || '')) ?? 'GBP';
}

export function canCheckoutSketchplanCurrency(currency: SketchplanCurrency): boolean {
  return SKETCHPLAN_CHECKOUT_CURRENCIES.includes(currency);
}

export interface DirectCardQuote {
  propertyType: SketchplanPropertyType;
  sizeBand: SketchplanSizeBand;
  contactSales: boolean;
  contactReason: 'size' | null;
  currency: SketchplanCurrency;
  /** Equivalent residential price for this size, in minor units. null if a quote is required. */
  residentialPence: number | null;
  /** The commercial surcharge in minor units. 0 for residential and account rates. */
  commercialUpliftPence: number;
  /**
   * Type + size price before express, in minor units (pence or cents).
   * null if a quote is required.
   */
  basePence: number | null;
  expressPence: number;
  /** Final chargeable amount in minor units. */
  totalPence: number | null;
  /** Extra tax added at checkout — always 0; list prices are charged as shown. */
  taxPence: number;
  turnaround: SketchplanTurnaround;
  usedCustomRate: boolean;
}

/**
 * Server-equivalent quote used by the UI. The Edge Function / SQL
 * `sketchplan_quote()` recalculates this independently from the seeded
 * price table and never trusts a browser-supplied amount.
 *
 * When both are given, `sqFt` wins over `sizeBandId` (the SQL does the same).
 */
export function calculateDirectCardPrice(input: {
  propertyType: SketchplanPropertyType;
  sizeBandId?: SketchplanSizeBandId | string | null;
  sqFt?: number | null;
  turnaround?: SketchplanTurnaround;
  currency?: SketchplanCurrency | string | null;
  /** Negotiated all-in GBP rate for a logged-in client. Never applied to guests / USD. */
  customPlanRatePence?: number | null;
}): DirectCardQuote {
  const propertyType = input.propertyType === 'commercial' ? 'commercial' : 'residential';
  const currency = normalizeSketchplanCurrency(input.currency);
  const hasSqFt = input.sqFt != null && Number.isFinite(input.sqFt) && input.sqFt > 0;
  const sizeBand =
    (hasSqFt ? getSizeBand(input.sqFt as number) : getSizeBandById(input.sizeBandId)) ||
    getSizeBand(0);
  const turnaround: SketchplanTurnaround = input.turnaround === 'express' ? 'express' : 'standard';
  const custom =
    currency === 'GBP' &&
    input.customPlanRatePence != null &&
    Number.isFinite(input.customPlanRatePence) &&
    input.customPlanRatePence >= 0
      ? Math.round(input.customPlanRatePence)
      : null;

  if (custom != null) {
    const totalPence = turnaround === 'express' ? Math.round(custom * SKETCHPLAN_EXPRESS_MULTIPLIER) : custom;
    return {
      propertyType,
      sizeBand,
      contactSales: false,
      contactReason: null,
      currency: 'GBP',
      residentialPence: custom,
      commercialUpliftPence: 0,
      basePence: custom,
      expressPence: totalPence - custom,
      totalPence,
      taxPence: 0,
      turnaround,
      usedCustomRate: true,
    };
  }

  const residentialPence = listPriceMinor('residential', sizeBand.id, currency);
  const basePence = listPriceMinor(propertyType, sizeBand.id, currency);
  if (basePence == null || residentialPence == null) {
    return {
      propertyType,
      sizeBand,
      contactSales: true,
      contactReason: 'size',
      currency,
      residentialPence: null,
      commercialUpliftPence: 0,
      basePence: null,
      expressPence: 0,
      totalPence: null,
      taxPence: 0,
      turnaround,
      usedCustomRate: false,
    };
  }

  const totalPence = turnaround === 'express' ? Math.round(basePence * SKETCHPLAN_EXPRESS_MULTIPLIER) : basePence;
  return {
    propertyType,
    sizeBand,
    contactSales: false,
    contactReason: null,
    currency,
    residentialPence,
    commercialUpliftPence: basePence - residentialPence,
    basePence,
    expressPence: totalPence - basePence,
    totalPence,
    taxPence: 0,
    turnaround,
    usedCustomRate: false,
  };
}

/** Credits charged for a logged-in prepaid order at the current £/credit rate. */
export function creditsForPence(pence: number, creditPriceGbp = 0.85): number {
  if (!Number.isFinite(pence) || pence <= 0) return 0;
  return Math.ceil(pence / 100 / creditPriceGbp);
}

/** Guess display currency from locale/country. EUR checkout is still gated. */
export function detectSketchplanCurrency(): SketchplanCurrency {
  return detectBillingCurrency({ stored: readPersistedBillingCurrency() });
}

export interface SketchplanPriceBandRow {
  property_type: SketchplanPropertyType;
  size_band: SketchplanSizeBandId;
  max_sq_ft: number | null;
  price_pence: number | null;
  price_usd_cents: number | null;
  price_eur_cents: number | null;
  label: string;
}

/** Rows used to seed `public.sketchplan_price_bands`. */
export function sketchplanPriceBandRows(): SketchplanPriceBandRow[] {
  const rows: SketchplanPriceBandRow[] = [];
  for (const type of ['residential', 'commercial'] as const) {
    for (const band of SKETCHPLAN_SIZE_BANDS) {
      rows.push({
        property_type: type,
        size_band: band.id,
        max_sq_ft: band.maxSqFt,
        price_pence: listPriceMinor(type, band.id, 'GBP'),
        price_usd_cents: listPriceMinor(type, band.id, 'USD'),
        price_eur_cents: listPriceMinor(type, band.id, 'EUR'),
        label: band.label,
      });
    }
  }
  return rows;
}
